import React, { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  ChevronLeft,
  Plus,
  Trash2,
} from "lucide-react";

import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import MultiSelect from "components/forms/MultiSelect";

const deepClone = (obj) => JSON.parse(JSON.stringify(obj == null ? {} : obj));

const toQuestionLangFieldKey = (langCode) => {
  const upper = String(langCode || "").toUpperCase();
  if (upper === "EN") return "question";
  return upper.toLowerCase();
};

const clamp = (num, min, max) =>
  Math.max(min, Math.min(max, Number.isFinite(Number(num)) ? Number(num) : min));

const collectExistingLangsFromFeedback = (feedback) => {
  const langs = new Set();
  (Array.isArray(feedback) ? feedback : []).forEach((set) => {
    // Check question-level language fields
    (Array.isArray(set?.questions) ? set.questions : []).forEach((q) => {
      Object.keys(q || {}).forEach((k) => {
        // Check for language-specific question fields (fr, por, es, etc.)
        if (k.length <= 3 && k !== "q" && k !== "s" && k !== "id" && k !== "question") {
          langs.add(k.toUpperCase());
        }
      });
      
      // Check select option labels
      if (q?.select?.options) {
        (Array.isArray(q.select.options) ? q.select.options : []).forEach((opt) => {
          if (opt?.label && typeof opt.label === "object") {
            Object.keys(opt.label).forEach((langCode) => {
              if (langCode !== "EN" && langCode.length <= 3) {
                langs.add(langCode.toUpperCase());
              }
            });
          }
          // Check option-level follow-up translations
          if (opt?.follow_up && typeof opt.follow_up === "object") {
            Object.keys(opt.follow_up).forEach((k) => {
              if (k !== "question" && k !== "prompt") {
                const upper = String(k).toUpperCase();
                if (upper.length <= 3) langs.add(upper);
              }
            });
          }
        });
      }
      
      // Check group titles
      if (q?.select?.group_titles) {
        Object.values(q.select.group_titles).forEach((groupTitle) => {
          if (groupTitle && typeof groupTitle === "object") {
            Object.keys(groupTitle).forEach((langCode) => {
              if (langCode !== "EN" && langCode.length <= 3) {
                langs.add(langCode.toUpperCase());
              }
            });
          }
        });
      }
      
      // Check AI follow-up translations
      if (q?.ai_follow_up?.questions) {
        (Array.isArray(q.ai_follow_up.questions) ? q.ai_follow_up.questions : []).forEach((fq) => {
          Object.keys(fq || {}).forEach((k) => {
            if (k.length <= 3 && k !== "q" && k !== "question" && k !== "prompt") {
              langs.add(k.toUpperCase());
            }
          });
        });
      }
    });
  });
  return Array.from(langs);
};

const DEFAULT_PROMPT =
  "What follow-up question would a world-class culture consultant ask based on the feedback given that would help to get more context around this area and also help generate recommendations for this organization? Make sure the response is simple enough for a 7th-grader to understand. Only return the question itself.";

const ensureRenumbering = (feedbackSets) => {
  const next = Array.isArray(feedbackSets) ? feedbackSets : [];
  next.forEach((setItem, setIdx) => {
    setItem.id = setIdx + 1;
    if (!setItem.name) setItem.name = "feedbackQuestion";
    const questions = Array.isArray(setItem.questions) ? setItem.questions : [];
    questions.forEach((q, idx) => {
      q.id = idx + 1;
      q.q = Number.isFinite(Number(q.q)) ? clamp(Number(q.q), 1, 10) : 1;
      q.s = Number.isFinite(Number(q.s)) && Number(q.s) >= 1 ? Number(q.s) : idx + 1;
      const ref = q.ask_on_ref && typeof q.ask_on_ref === "object" ? q.ask_on_ref : {};
      const min = clamp(Array.isArray(ref.value) ? ref.value[0] : 0, 0, 10);
      const max = clamp(Array.isArray(ref.value) ? ref.value[1] : 10, 0, 10);
      const fixedMin = Math.min(min, max);
      const fixedMax = Math.max(min, max);
      q.ask_on_ref = {
        enabled: !!ref.enabled,
        ref_type: ref.ref_type || "outcome",
        ref_question_id: ref.ref_question_id || `${q.q}.${q.s}`,
        value: [fixedMin, fixedMax],
      };
      const ai = q.ai_follow_up && typeof q.ai_follow_up === "object" ? q.ai_follow_up : {};
      let aiQs = Array.isArray(ai.questions) ? ai.questions.slice(0, 1) : [];
      if (ai.enabled && aiQs.length === 0) {
        aiQs = [{ q: 1, question: "", prompt: DEFAULT_PROMPT }];
      }
      aiQs = aiQs.slice(0, 1).map((fq) => ({
        q: 1,
        question: typeof fq.question === "string" ? fq.question : "",
        prompt:
          typeof fq.prompt === "string" && fq.prompt.length > 0
            ? fq.prompt
            : DEFAULT_PROMPT,
        ...Object.keys(fq || {}).reduce((acc, k) => {
          if (k !== "q" && k !== "prompt" && k !== "question") acc[k] = fq[k];
          return acc;
        }, {}),
      }));
      q.ai_follow_up = { enabled: !!ai.enabled, questions: aiQs };
      const sel = q.select && typeof q.select === "object" ? q.select : {};
      const options = Array.isArray(sel.options) ? sel.options : [];
      const safeOptions = options.map((opt, oi) => {
        const o = opt && typeof opt === "object" ? { ...opt } : {};
        if (!o.id) o.id = `${oi + 1}`;
        // Handle multilingual labels - convert string to object if needed
        if (typeof o.label === "string") {
          o.label = { EN: o.label };
        } else if (!o.label || typeof o.label !== "object") {
          o.label = { EN: "" };
        }
        if (o.group && typeof o.group !== "string") delete o.group;
        // Normalize option-level follow-up container if enabled at question level
        const hasOptionFU = !!(q?.option_follow_up && q.option_follow_up.enabled);
        if (hasOptionFU) {
          const fu = o.follow_up && typeof o.follow_up === "object" ? { ...o.follow_up } : {};
          if (typeof fu.EN !== "string") fu.EN = "";
          o.follow_up = fu;
        }
        return o;
      });
      
      // Clean up group titles - only keep titles for groups that actually exist in options
      const existingGroups = new Set();
      safeOptions.forEach(opt => {
        if (opt.group && typeof opt.group === "string" && opt.group.trim()) {
          existingGroups.add(opt.group.trim());
        }
      });
      
      const groupTitles = sel.group_titles && typeof sel.group_titles === "object" ? sel.group_titles : {};
      const cleanedGroupTitles = {};
      
      // Keep existing titles for groups that still exist
      Object.keys(groupTitles).forEach(groupName => {
        if (existingGroups.has(groupName)) {
          cleanedGroupTitles[groupName] = groupTitles[groupName];
        }
      });
      
      // Create default titles for groups that exist but don't have titles yet
      existingGroups.forEach(groupName => {
        if (!cleanedGroupTitles[groupName]) {
          cleanedGroupTitles[groupName] = { EN: groupName };
        }
      });
      
      q.select = { enabled: !!sel.enabled, options: safeOptions, group_titles: cleanedGroupTitles };
      const ofu = q.option_follow_up && typeof q.option_follow_up === "object" ? q.option_follow_up : {};
      q.option_follow_up = { enabled: !!ofu.enabled };
    });
    setItem.questions = questions;
  });
  return next;
};

const LANGUAGE_OPTIONS = [
  { key: "FR", value: "FR", text: "French (FR)" },
  { key: "POR", value: "POR", text: "Portuguese (POR)" },
  { key: "ES", value: "ES", text: "Spanish (ES)" },
];

const MultiLabel = ({ label, children }) => (
  <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
    <span>{label}</span>
    <div className="space-y-3">{children}</div>
  </label>
);

const RangeInput = ({ label, value, onChange, min = 0, max = 10 }) => (
  <label className="flex flex-col gap-2 text-sm font-medium text-foreground">
    <span>{label}</span>
    <Input type="number" min={min} max={max} value={value} onChange={onChange} />
  </label>
);

export default function OutcomeFeedbackStructureManagerModal({ open, onClose, feedback, onSave, languages }) {
  const [working, setWorking] = useState([]);
  const [selectedSetIndex, setSelectedSetIndex] = useState(0);
  const [selectedLangs, setSelectedLangs] = useState([]);

  useEffect(() => {
    if (open) {
      const baseArray = Array.isArray(feedback) ? deepClone(feedback) : [];
      const base = baseArray.length > 0 ? baseArray : [{ id: 1, name: "feedbackQuestion", questions: [] }];
      const normalized = ensureRenumbering(base);
      setWorking(normalized);
      setSelectedSetIndex(0);
      const fromProps = Array.isArray(languages)
        ? languages.map((l) => String(l).toUpperCase()).filter((l) => l !== "EN")
        : [];
      const fromData = collectExistingLangsFromFeedback(normalized).filter((l) => l !== "EN");
      const merged = Array.from(new Set([...(fromProps || []), ...(fromData || [])]));
      setSelectedLangs(merged);
    }
  }, [open, feedback, languages]);

  const currentSet = working?.[selectedSetIndex] || null;
  const currentQuestions = Array.isArray(currentSet?.questions) ? currentSet.questions : [];

  const effectiveLanguages = useMemo(() => {
    const fromData = collectExistingLangsFromFeedback(working);
    const merged = new Set(["EN", ...(selectedLangs || []), ...fromData]);
    return Array.from(merged);
  }, [selectedLangs, working]);

  const languageFieldDefs = useMemo(() => {
    return effectiveLanguages.map((LC) => ({ code: LC, qField: toQuestionLangFieldKey(LC) }));
  }, [effectiveLanguages]);

  const addQuestion = (sIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const setObj = { ...(next[sIndex] || {}) };
      const arr = Array.isArray(setObj.questions) ? [...setObj.questions] : [];
      const q = { question: "", allowSkip: true, q: 1, s: arr.length + 1, id: arr.length + 1 };
      languageFieldDefs.forEach(({ qField }) => {
        if (qField !== "question") q[qField] = "";
      });
      q.ask_on_ref = {
        enabled: true,
        value: [0, 10],
        ref_type: "outcome",
        ref_question_id: `${q.q}.${q.s}`,
      };
      q.ai_follow_up = { enabled: false, questions: [] };
      q.select = { enabled: false, options: [], group_titles: {} };
      arr.push(q);
      setObj.questions = arr;
      next[sIndex] = setObj;
      return ensureRenumbering(next);
    });
  };

  const removeQuestion = (sIndex, qIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const setObj = { ...(next[sIndex] || {}) };
      const arr = Array.isArray(setObj.questions) ? [...setObj.questions] : [];
      arr.splice(qIndex, 1);
      setObj.questions = arr;
      next[sIndex] = setObj;
      return ensureRenumbering(next);
    });
  };

  const setQuestionField = (sIndex, qIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const setObj = { ...(next[sIndex] || {}) };
      const arr = Array.isArray(setObj.questions) ? [...setObj.questions] : [];
      const q = { ...(arr[qIndex] || {}) };
      q[field] = value;
      if (field === "q" || field === "s") {
        const ref = q.ask_on_ref && typeof q.ask_on_ref === "object" ? q.ask_on_ref : {};
        q.ask_on_ref = { ...ref, ref_question_id: `${q.q || 1}.${q.s || qIndex + 1}` };
      }
      arr[qIndex] = q;
      setObj.questions = arr;
      next[sIndex] = setObj;
      return next;
    });
  };

  const setAskOnRef = (sIndex, qIndex, partial) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const arr = Array.isArray(next?.[sIndex]?.questions) ? [...next[sIndex].questions] : [];
      const q = { ...(arr[qIndex] || {}) };
      const ref = q.ask_on_ref && typeof q.ask_on_ref === "object" ? q.ask_on_ref : {};
      const merged = { ...ref, ...partial };
      const min = clamp(Array.isArray(merged.value) ? merged.value[0] : 0, 0, 10);
      const max = clamp(Array.isArray(merged.value) ? merged.value[1] : 10, 0, 10);
      merged.value = [Math.min(min, max), Math.max(min, max)];
      if (!merged.ref_type) merged.ref_type = "outcome";
      if (!merged.ref_question_id) merged.ref_question_id = `${q.q || 1}.${q.s || qIndex + 1}`;
      q.ask_on_ref = merged;
      arr[qIndex] = q;
      next[sIndex].questions = arr;
      return next;
    });
  };

  const toggleFollowUpEnabled = (sIndex, qIndex, enabled) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      if (enabled) {
        const first = (q?.ai_follow_up?.questions && q.ai_follow_up.questions[0]) || {};
        q.ai_follow_up = {
          enabled: true,
          questions: [{ q: 1, question: first.question || "", prompt: DEFAULT_PROMPT }],
        };
      } else {
        q.ai_follow_up = { enabled: false, questions: [] };
      }
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const setFollowUpField = (sIndex, qIndex, field, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const arr = Array.isArray(q?.ai_follow_up?.questions) ? [...q.ai_follow_up.questions] : [];
      const item = { ...(arr[0] || { q: 1, question: "", prompt: DEFAULT_PROMPT }) };
      item[field] = value;
      if (!item.prompt) item.prompt = DEFAULT_PROMPT;
      arr[0] = item;
      q.ai_follow_up = { enabled: !!q?.ai_follow_up?.enabled, questions: arr.slice(0, 1) };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const addSelectOption = (sIndex, qIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: true, options: [], group_titles: {} };
      const arr = Array.isArray(sel.options) ? [...sel.options] : [];
      const newOpt = { id: uuidv4(), label: { EN: "" } };
      if (q?.option_follow_up?.enabled) {
        newOpt.follow_up = { EN: "" };
      }
      arr.push(newOpt);
      q.select = { ...sel, enabled: true, options: arr };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const removeSelectOption = (sIndex, qIndex, oIndex) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: true, options: [], group_titles: {} };
      const arr = Array.isArray(sel.options) ? [...sel.options] : [];
      arr.splice(oIndex, 1);
      q.select = { ...sel, options: arr };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const setSelectOptionField = (sIndex, qIndex, oIndex, field, langCodeOrValue, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: true, options: [], group_titles: {} };
      const arr = Array.isArray(sel.options) ? [...sel.options] : [];
      const item = { ...(arr[oIndex] || {}) };
      
      // Handle multilingual labels
      if (field === "label" && langCodeOrValue && langCodeOrValue !== value) {
        // langCodeOrValue is the language code, value is the actual value
        if (!item.label || typeof item.label !== "object") {
          item.label = { EN: "" };
        }
        item.label[langCodeOrValue] = value;
      } else {
        // Regular field update
        item[field] = langCodeOrValue;
      }
      
      arr[oIndex] = item;
      q.select = { ...sel, options: arr };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const setGroupTitle = (sIndex, qIndex, groupName, langCode, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: true, options: [], group_titles: {} };
      const gt = { ...(sel.group_titles || {}) };
      const key = String(langCode).toUpperCase();
      const entry = { EN: gt[groupName]?.EN || groupName };
      selectedLangs.forEach((LC) => {
        const upper = String(LC).toUpperCase();
        entry[upper] = gt[groupName]?.[upper] || "";
      });
      entry[key] = value;
      gt[groupName] = entry;
      q.select = { ...sel, group_titles: gt };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const toggleSelectEnabled = (sIndex, qIndex, enabled) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: false, options: [], group_titles: {} };
      q.select = { ...sel, enabled: !!enabled };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const toggleOptionFollowUpEnabled = (sIndex, qIndex, enabled) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const current = q.option_follow_up && typeof q.option_follow_up === "object" ? q.option_follow_up : {};
      q.option_follow_up = { ...current, enabled: !!enabled };
      // Initialize follow_up containers on options when enabling
      if (q?.select?.options && !!enabled) {
        const opts = (q.select.options || []).map((opt) => {
          const o = { ...(opt || {}) };
          const fu = o.follow_up && typeof o.follow_up === "object" ? { ...o.follow_up } : {};
          if (typeof fu.EN !== "string") fu.EN = "";
          o.follow_up = fu;
          return o;
        });
        q.select = { ...(q.select || {}), options: opts };
      }
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const setOptionFollowUpField = (sIndex, qIndex, oIndex, langCode, value) => {
    setWorking((prev) => {
      const next = deepClone(prev);
      const q = { ...(next?.[sIndex]?.questions?.[qIndex] || {}) };
      const sel = q.select && typeof q.select === "object" ? q.select : { enabled: true, options: [], group_titles: {} };
      const arr = Array.isArray(sel.options) ? [...sel.options] : [];
      const item = { ...(arr[oIndex] || {}) };
      const fu = item.follow_up && typeof item.follow_up === "object" ? { ...item.follow_up } : {};
      const key = String(langCode).toUpperCase();
      fu[key] = value;
      item.follow_up = fu;
      arr[oIndex] = item;
      q.select = { ...sel, options: arr };
      next[sIndex].questions[qIndex] = q;
      return next;
    });
  };

  const setFollowUpPrompt = (sIndex, qIndex, value) => {
    setFollowUpField(sIndex, qIndex, "prompt", value);
  };

  const getGroupsFromOptions = (options) => {
    const arr = Array.isArray(options) ? options : [];
    const set = new Set();
    arr.forEach((o) => {
      const g = (o && typeof o.group === "string" ? o.group : "").trim();
      if (g.length > 0) set.add(g);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  const renderGroupTitles = (qObj, sIndex, qIndex) => {
    const groups = getGroupsFromOptions(qObj?.select?.options);
    if (groups.length === 0) {
      return <p className="text-sm text-muted-foreground">Assign a group to at least one option to edit titles.</p>;
    }
    return (
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={`gt-${g}`} className="rounded-lg border border-border/60 bg-muted/20 p-4">
            <h5 className="text-sm font-semibold">{g}</h5>
            <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              <Input
                placeholder="Title (EN)"
                value={qObj?.select?.group_titles?.[g]?.EN || g}
                onChange={(e) => setGroupTitle(sIndex, qIndex, g, "EN", e.target.value)}
              />
              {selectedLangs.map((LC) => (
                <Input
                  key={`gt-${g}-${LC}`}
                  placeholder={`Title (${LC})`}
                  value={qObj?.select?.group_titles?.[g]?.[LC] || ""}
                  onChange={(e) => setGroupTitle(sIndex, qIndex, g, LC, e.target.value)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleSave = () => {
    const data = ensureRenumbering(deepClone(working));
    onSave && onSave({ feedback: data, languages: selectedLangs });
    onClose && onClose();
  };

  const handleDialogChange = (nextOpen) => {
    if (!nextOpen) onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-h-[90dvh] w-full max-w-6xl overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Manage Outcome Feedback Structure</DialogTitle>
          <DialogDescription>
            Manage feedback questions, reference triggers, dropdown options, and multilingual follow-ups.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 overflow-y-auto px-6 pb-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-base">Languages</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <MultiSelect
                options={LANGUAGE_OPTIONS}
                value={selectedLangs}
                onChange={(value) =>
                  setSelectedLangs(Array.isArray(value) ? value.map((v) => String(v).toUpperCase()).filter((v) => v !== "EN") : [])
                }
                placeholder="Select languages"
                className="bg-white"
              />
              <p className="text-xs text-muted-foreground">English (EN) is always included.</p>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="flex flex-wrap items-center justify-between gap-3 pb-4">
              <CardTitle className="text-base">Questions</CardTitle>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => addQuestion(selectedSetIndex)}
                className="inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add question
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {(currentQuestions || []).length === 0 && (
                <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
                  Add outcome feedback questions to customize follow-up logic.
                </div>
              )}

              {(currentQuestions || []).map((q, qi) => (
                <div key={`q-${qi}`} className="space-y-5 rounded-lg border border-border/60 bg-card/40 p-6">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground">
                      Question {qi + 1} (id {q?.id})
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="gap-1"
                        onClick={() =>
                          setQuestionField(selectedSetIndex, qi, "s", Math.max(1, Number(q?.s || qi + 1) - 1))
                        }
                      >
                        <ChevronLeft className="h-3.5 w-3.5" /> Shift up
                      </Button>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        onClick={() => removeQuestion(selectedSetIndex, qi)}
                        className="text-destructive"
                        aria-label="Remove question"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <RangeInput
                      label="Outcome reference (q id, 1-10)"
                      value={q?.q || 1}
                      onChange={(e) =>
                        setQuestionField(selectedSetIndex, qi, "q", clamp(e.target.value, 1, 10))
                      }
                      min={1}
                      max={10}
                    />
                    <RangeInput
                      label="Sequence id (s)"
                      value={q?.s || qi + 1}
                      onChange={(e) =>
                        setQuestionField(
                          selectedSetIndex,
                          qi,
                          "s",
                          Math.max(1, Number(e.target.value) || 1),
                        )
                      }
                      min={1}
                    />
                    <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Checkbox
                        checked={!!q?.allowSkip}
                        onCheckedChange={(checked) =>
                          setQuestionField(selectedSetIndex, qi, "allowSkip", !!checked)
                        }
                      />
                      Allow skip
                    </label>
                    <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                      <Checkbox
                        checked={!!q?.select?.enabled}
                        onCheckedChange={(checked) =>
                          toggleSelectEnabled(selectedSetIndex, qi, !!checked)
                        }
                      />
                      Response uses dropdown
                    </label>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    {languageFieldDefs.map(({ code, qField }) => (
                      <MultiLabel key={`qlang-${code}`} label={code === "EN" ? "English" : code}>
                        <Textarea
                          placeholder={code === "EN" ? "Question (EN)" : `Translation (${code})`}
                          rows={code === "EN" ? 4 : 3}
                          value={(q && (q[qField] ?? ""))}
                          onChange={(e) =>
                            setQuestionField(selectedSetIndex, qi, qField, e.target.value)
                          }
                          className="min-h-[120px]"
                        />
                      </MultiLabel>
                    ))}
                  </div>

                  {q?.select?.enabled && (
                    <div className="space-y-4 rounded-lg border border-border/60 bg-muted/10 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h4 className="text-sm font-semibold text-foreground">Dropdown options</h4>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => addSelectOption(selectedSetIndex, qi)}
                          className="inline-flex items-center gap-2"
                        >
                          <Plus className="h-4 w-4" /> Add option
                        </Button>
                      </div>
                      <div className="space-y-4">
                        {(q?.select?.options || []).map((opt, oi) => (
                          <div key={opt.id || oi} className="space-y-3 rounded-lg border border-border/60 bg-card/20 p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center text-xs font-semibold text-muted-foreground">
                                <span className="inline-flex h-7 min-w-[2.5rem] items-center justify-center rounded-md bg-muted px-2">
                                  {opt.id || oi + 1}
                                </span>
                              </div>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => removeSelectOption(selectedSetIndex, qi, oi)}
                                className="text-destructive"
                                aria-label="Remove option"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            
                            <div className="space-y-3">
                              <h6 className="text-sm font-medium text-foreground">Labels</h6>
                              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                <Input
                                  placeholder="Label (EN)"
                                  value={opt.label?.EN || ""}
                                  onChange={(e) =>
                                    setSelectOptionField(selectedSetIndex, qi, oi, "label", "EN", e.target.value)
                                  }
                                />
                                {selectedLangs.map((LC) => (
                                  <Input
                                    key={`opt-label-${LC}-${oi}`}
                                    placeholder={`Label (${LC})`}
                                    value={opt.label?.[LC] || ""}
                                    onChange={(e) =>
                                      setSelectOptionField(selectedSetIndex, qi, oi, "label", LC, e.target.value)
                                    }
                                  />
                                ))}
                              </div>
                            </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <h6 className="text-sm font-medium text-foreground">Option follow-up</h6>
                              {oi === 0 && (
                                <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                                  <Checkbox
                                    checked={!!q?.option_follow_up?.enabled}
                                    onCheckedChange={(checked) =>
                                      toggleOptionFollowUpEnabled(selectedSetIndex, qi, !!checked)
                                    }
                                  />
                                  Enabled for all options
                                </label>
                              )}
                            </div>
                            {q?.option_follow_up?.enabled && (
                              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                                <Textarea
                                  placeholder="Follow-up (EN)"
                                  rows={2}
                                  value={opt.follow_up?.EN || ""}
                                  onChange={(e) =>
                                    setOptionFollowUpField(selectedSetIndex, qi, oi, "EN", e.target.value)
                                  }
                                />
                                {selectedLangs.map((LC) => (
                                  <Textarea
                                    key={`opt-fu-${LC}-${oi}`}
                                    placeholder={`Follow-up (${LC})`}
                                    rows={2}
                                    value={opt.follow_up?.[LC] || ""}
                                    onChange={(e) =>
                                      setOptionFollowUpField(selectedSetIndex, qi, oi, LC, e.target.value)
                                    }
                                  />
                                ))}
                              </div>
                            )}
                          </div>

                            <div className="space-y-2">
                              <h6 className="text-sm font-medium text-foreground">Group</h6>
                              <Input
                                placeholder="Group (optional)"
                                value={opt.group || ""}
                                onChange={(e) =>
                                  setSelectOptionField(selectedSetIndex, qi, oi, "group", e.target.value)
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <h5 className="text-sm font-semibold text-foreground">Group titles</h5>
                        {renderGroupTitles(q, selectedSetIndex, qi)}
                      </div>
                    </div>
                  )}

                  <div className="space-y-4 rounded-lg border border-border/60 bg-card/30 p-4">
                    <h4 className="text-sm font-semibold text-foreground">Ask-on reference</h4>
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Checkbox
                          checked={!!q?.ask_on_ref?.enabled}
                          onCheckedChange={(checked) =>
                            setAskOnRef(selectedSetIndex, qi, { enabled: !!checked })
                          }
                        />
                        Enabled
                      </label>
                      <RangeInput
                        label="Min (0-10)"
                        value={Array.isArray(q?.ask_on_ref?.value) ? q.ask_on_ref.value[0] : 0}
                        onChange={(e) =>
                          setAskOnRef(selectedSetIndex, qi, {
                            value: [clamp(e.target.value, 0, 10), Array.isArray(q?.ask_on_ref?.value) ? q.ask_on_ref.value[1] : 10],
                          })
                        }
                      />
                      <RangeInput
                        label="Max (0-10)"
                        value={Array.isArray(q?.ask_on_ref?.value) ? q.ask_on_ref.value[1] : 10}
                        onChange={(e) =>
                          setAskOnRef(selectedSetIndex, qi, {
                            value: [
                              Array.isArray(q?.ask_on_ref?.value) ? q.ask_on_ref.value[0] : 0,
                              clamp(e.target.value, 0, 10),
                            ],
                          })
                        }
                      />
                      <div className="flex flex-col text-sm text-muted-foreground">
                        <span>Reference question id</span>
                        <span className="font-mono text-xs text-foreground">
                          {`${q?.q || 1}.${q?.s || qi + 1}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 rounded-lg border border-border/60 bg-muted/10 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h4 className="text-sm font-semibold text-foreground">AI follow-up</h4>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <Checkbox
                          checked={!!q?.ai_follow_up?.enabled}
                          onCheckedChange={(checked) =>
                            toggleFollowUpEnabled(selectedSetIndex, qi, !!checked)
                          }
                        />
                        Enabled
                      </label>
                    </div>
                    {q?.ai_follow_up?.enabled && (
                      <div className="space-y-3">
                        <MultiLabel label="Follow-up (EN)">
                          <Textarea
                            rows={3}
                            value={q?.ai_follow_up?.questions?.[0]?.question || ""}
                            onChange={(e) =>
                              setFollowUpField(selectedSetIndex, qi, "question", e.target.value)
                            }
                          />
                        </MultiLabel>
                        <MultiLabel label="Prompt">
                          <Textarea
                            rows={3}
                            value={q?.ai_follow_up?.questions?.[0]?.prompt || DEFAULT_PROMPT}
                            onChange={(e) => setFollowUpPrompt(selectedSetIndex, qi, e.target.value)}
                          />
                        </MultiLabel>
                        {selectedLangs.map((LC) => {
                          const key = String(LC).toLowerCase();
                          return (
                            <MultiLabel key={`fq-lang-${key}`} label={`Translation (${LC})`}>
                              <Textarea
                                rows={2}
                                value={q?.ai_follow_up?.questions?.[0]?.[key] || ""}
                                onChange={(e) =>
                                  setFollowUpField(selectedSetIndex, qi, key, e.target.value)
                                }
                              />
                            </MultiLabel>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="border-t border-border/60 bg-muted/10 px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


